import { Placement } from '@floating-ui/react';
import { RecurrenceFormMode } from '@griply/types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PopupMenu,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuTitle,
} from 'shared/components/ui/popup-menu';
import { RadioInput } from 'shared/components/ui/radio-input';
import { InteractionType } from 'shared/types/data-type';
import { HabitSchedule, ScheduleFrequency } from 'shared/types/habit-schedule';
import { TaskType } from 'shared/types/task-base';
import { WeekDays } from 'shared/types/week-days';

import * as Styled from './repeat-form.style';
import { RruleForm } from './rrule-form';
import { TargetForm } from './target-form';

export type RepeatFormProps = {
  initialRrule?: string;
  initialFrequency?: ScheduleFrequency | null;
  type: TaskType;
  weekStartsOn: WeekDays;
  onChange: (schedule: HabitSchedule | null) => void;
  onChangeType?: (type: TaskType.Habit | TaskType.Repeating) => void;
  onPremium: () => void;
  isTargetPremium: boolean;
  isCreateHabitPremium: boolean;
  onClose: () => void;
  referenceElement: React.RefObject<HTMLElement>;
  position?: Placement;
  hasParentWidth?: boolean;
  interactionType?: InteractionType;
};

export const RepeatForm: React.FC<RepeatFormProps> = ({
  initialRrule,
  initialFrequency,
  type,
  weekStartsOn,
  onChange,
  onChangeType,
  onPremium,
  isTargetPremium,
  isCreateHabitPremium,
  onClose,
  referenceElement,
  position,
  hasParentWidth,
  interactionType,
}) => {
  const { t } = useTranslation();
  const [option, setOption] = useState<RecurrenceFormMode>(
    initialFrequency ? RecurrenceFormMode.Target : RecurrenceFormMode.RRule,
  );

  const handleSelectRRule = () => {
    setOption(RecurrenceFormMode.RRule);
  };

  const handleSelectTarget = () => {
    if (isTargetPremium) {
      onPremium();
      return;
    }

    setOption(RecurrenceFormMode.Target);
  };

  return (
    <PopupMenu
      referenceElement={referenceElement}
      onOutside={onClose}
      position={position}
      hasParentWidth={hasParentWidth}
      interactionType={interactionType}
    >
      <PopupMenuList hasBottomBorder>
        <PopupMenuTitle
          title={t('general.labels.habit.repeat.options.schedule-type.label')}
        />
        <PopupMenuListItem>
          <Styled.RadioInputContainer>
            <RadioInput
              checked={option === RecurrenceFormMode.RRule}
              onChange={handleSelectRRule}
            >
              {t('general.labels.habit.repeat.options.rrule.label')}
            </RadioInput>
          </Styled.RadioInputContainer>
        </PopupMenuListItem>

        <PopupMenuListItem>
          <Styled.RadioInputContainer>
            <RadioInput
              checked={option === RecurrenceFormMode.Target}
              onChange={handleSelectTarget}
            >
              {t('general.labels.habit.repeat.options.target.label')}
              {isTargetPremium && (
                <Styled.OptionPremiumLabel>
                  {t('general.labels.premium')}
                </Styled.OptionPremiumLabel>
              )}
            </RadioInput>
          </Styled.RadioInputContainer>
        </PopupMenuListItem>
      </PopupMenuList>

      <Styled.Container>
        {option === RecurrenceFormMode.RRule && (
          <RruleForm
            type={type}
            initialRrule={initialRrule}
            weekStartsOn={weekStartsOn}
            onChange={onChange}
            onChangeType={onChangeType}
            createHabitPremium={isCreateHabitPremium}
            onCancel={onClose}
          />
        )}

        {option === RecurrenceFormMode.Target && (
          <TargetForm
            type={type}
            initialFrequency={initialFrequency}
            onChange={onChange}
            onChangeType={onChangeType}
            createHabitPremium={isCreateHabitPremium}
            onCancel={onClose}
          />
        )}
      </Styled.Container>
    </PopupMenu>
  );
};
